// @ts-nocheck
// TODO: tighten types after migration
import React, { useState } from 'react';
// eslint-disable-next-line no-unused-vars
import { LazyLoadImage } from "react-lazy-load-image-component";
import "../Blog/Post.css"; // Ensure global styles are available
import "./ImageSlideshow.css"; // Import specific component styles
import FullScreenImageViewer from "./FullScreenImageViewer";
import InlineMediaVideo from "./InlineMediaVideo";

const ImageSlideshow = ({ images }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [triggerRect, setTriggerRect] = useState(null);
    const imgRef = React.useRef(null);
    const wrapperRef = React.useRef(null);

    // Reset loaded state when index changes
    React.useEffect(() => {
        setImageLoaded(false);
    }, [currentIndex]);

    // Robust check for already loaded images (cache)
    React.useEffect(() => {
        if (imgRef.current && imgRef.current.complete) {
            setImageLoaded(true);
        }
    }, [currentIndex, images]);

    if (!images || images.length === 0) return null;

    const currentItem = images[currentIndex] || {};

    // Helper for ordinal suffix (1st, 2nd, 3rd...)
    const getOrdinalSuffix = (i) => {
        var j = i % 10,
            k = i % 100;
        if (j === 1 && k !== 11) {
            return "st";
        }
        if (j === 2 && k !== 12) {
            return "nd";
        }
        if (j === 3 && k !== 13) {
            return "rd";
        }
        return "th";
    };

    const handleMaximize = () => {
        if (wrapperRef.current) {
            setTriggerRect(wrapperRef.current.getBoundingClientRect());
        } else if (imgRef.current) {
            setTriggerRect(imgRef.current.getBoundingClientRect());
        }
        setIsFullScreen(true);
    };

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const handlePrev = () => {
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    return (
        <React.Fragment>
            <div className="mongodb-image-post-area my-10 block clear-both relative flow-root min-h-[250px]">
                {/* Image/Video Container - Full Width */}
                <div ref={wrapperRef} className="mongodb-image-wrapper w-full flex justify-center items-center relative mb-2.5">
                    {currentItem.isVideo ? (
                        <InlineMediaVideo
                            src={currentItem.src}
                            title={currentItem.title || currentItem.alt || ''}
                            poster={currentItem.poster || ''}
                            showTitle={false}
                            style={{ margin: 0 }}
                            isParentFullScreen={isFullScreen}
                        />
                    ) : (
                        <>
                            {!imageLoaded && (
                                <div className="blog-image-skeleton-loader"></div>
                            )}
                            <img
                                ref={imgRef}
                                className="mongodb-image-post-view w-full max-w-full h-auto rounded-[10px] block object-contain shadow-[0_4px_6px_rgba(0,0,0,0.05)]"
                                src={currentItem.src}
                                alt={currentItem.alt || `image-${currentIndex+1}`}
                                onLoad={() => setImageLoaded(true)}
                                onError={() => setImageLoaded(true)} // Hide skeleton on error
                                style={{ ...(!imageLoaded ? { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0 } : {}), cursor: 'zoom-in' }}
                                onClick={handleMaximize}
                            />
                        </>
                    )}
                </div>

                {/* Controls and Description Row */}
                <div className="mongodb-image-info-row flex justify-between items-center w-full gap-5 p-2.5 sm:gap-2.5">
                    {/* Description - Bottom Left */}
                    <p className="mongodb-image-title m-0 text-left flex-1 text-1rem leading-[1.5] text-[var(--post-content-font,#333)]">
                        <strong>
                            {currentIndex + 1}<sup>{getOrdinalSuffix(currentIndex + 1)}</sup> {currentItem.isVideo ? "video" : "image"}
                        </strong>
                        {currentItem.title ? `: ${currentItem.title}` : (currentItem.alt ? `: ${currentItem.alt}` : "")}
                    </p>

                    {/* Controls - Bottom Right */}
                    <div className="mongodb-image-post-controls flex gap-2.5 m-0 flex-shrink-0 items-center">
                        {/* Maximize Button - before arrow icons as requested */}
                        <button
                            className="mongodb-nav-btn maximize-btn appearance-none bg-white border border-[#e1e1e1] rounded-[8px] w-8 h-8 flex items-center justify-center cursor-pointer transition-all duration-200 shadow-[0_2px_4px_rgba(0,0,0,0.08)] p-0 relative outline-none hover:translate-y-[-1px] hover:shadow-[0_3px_6px_rgba(0,0,0,0.12)] hover:border-[#d1d1d1] hover:bg-[#fdfdfd] active:translate-y-0 active:shadow-[0_1px_2px_rgba(0,0,0,0.1)] disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-[#f9f9f9] disabled:shadow-none disabled:border-[#eee]"
                            onClick={handleMaximize}
                            aria-label="Maximize slideshow"
                            style={{ marginRight: '8px' }}
                        >
                            <i className="fi fi-br-expand text-[12px] leading-[1] flex items-center justify-center text-[#555] w-full h-full m-t-1"></i>
                        </button>
                        {/* Arrows */}
                        {images.length > 1 && (
                            <>
                                <button
                                    className="mongodb-nav-btn appearance-none bg-white border border-[#47b5ff] text-[#47b5ff] rounded-[8px] w-8 h-8 flex items-center justify-center cursor-pointer transition-all duration-200 shadow-[0_2px_4px_rgba(71,181,255,0.15)] p-0 relative outline-none hover:translate-y-[-1px] hover:shadow-[0_3px_6px_rgba(71,181,255,0.3)] hover:bg-[#47b5ff] hover:text-white active:translate-y-0 active:shadow-[0_1px_2px_rgba(71,181,255,0.2)] disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-[#f9f9f9] disabled:border-[#e1e1e1] disabled:text-[#aaa] disabled:shadow-none"
                                    onClick={handlePrev}
                                    disabled={currentIndex === 0}
                                    aria-label="Previous slide"
                                >
                                    <i className="fi fi-rr-caret-left text-[12px] leading-[1] flex items-center justify-center text-[#47b5ff] hover:text-inherit w-full h-full m-t-1 disabled:text-[#ccc]"></i>
                                </button>

                                <button
                                    className="mongodb-nav-btn appearance-none bg-white border border-[#47b5ff] text-[#47b5ff] rounded-[8px] w-8 h-8 flex items-center justify-center cursor-pointer transition-all duration-200 shadow-[0_2px_4px_rgba(71,181,255,0.15)] p-0 relative outline-none hover:translate-y-[-1px] hover:shadow-[0_3px_6px_rgba(71,181,255,0.3)] hover:bg-[#47b5ff] hover:text-white active:translate-y-0 active:shadow-[0_1px_2px_rgba(71,181,255,0.2)] disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-[#f9f9f9] disabled:border-[#e1e1e1] disabled:text-[#aaa] disabled:shadow-none"
                                    onClick={handleNext}
                                    disabled={currentIndex === images.length - 1}
                                    aria-label="Next slide"
                                >
                                    <i className="fi fi-rr-caret-right text-[12px] leading-[1] flex items-center justify-center text-[#47b5ff] hover:text-inherit w-full h-full m-t-1 disabled:text-[#ccc]"></i>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <FullScreenImageViewer
                isOpen={isFullScreen}
                onClose={() => setIsFullScreen(false)}
                images={images}
                initialIndex={currentIndex}
                triggerRect={triggerRect}
            />
        </React.Fragment>
    );
};

export default ImageSlideshow;
